import React, { createContext, useContext, useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { PRO_SHIPPING_CONFIG } from "@/lib/proShipping";

interface SubscriptionState {
  subscribed: boolean;
  isPro: boolean;
  plan: string | null;
  subscriptionEnd: string | null;
  loading: boolean;
  shippingCreditBalance: number;
  shippingCreditPeriodEnd: string | null;
  benefitsUsedThisMonth: number;
  benefitsCapRemaining: number;
  isFoundingSeller: boolean;
  foundingSellerFeeExpiresAt: string | null;
}

interface SubscriptionContextType extends SubscriptionState {
  checkSubscription: () => Promise<void>;
  openCheckout: () => void;
  openCustomerPortal: () => void;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export function SubscriptionProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<SubscriptionState>({
    subscribed: false,
    isPro: false,
    plan: null,
    subscriptionEnd: null,
    loading: true,
    shippingCreditBalance: 0,
    shippingCreditPeriodEnd: null,
    benefitsUsedThisMonth: 0,
    benefitsCapRemaining: PRO_SHIPPING_CONFIG.MONTHLY_BENEFIT_CAP_CENTS,
    isFoundingSeller: false,
    foundingSellerFeeExpiresAt: null,
  });
  const { toast } = useToast();

  const checkSubscription = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setState({ 
          subscribed: false, 
          isPro: false, 
          plan: null, 
          subscriptionEnd: null, 
          loading: false,
          shippingCreditBalance: 0,
          shippingCreditPeriodEnd: null,
          benefitsUsedThisMonth: 0,
          benefitsCapRemaining: PRO_SHIPPING_CONFIG.MONTHLY_BENEFIT_CAP_CENTS,
          isFoundingSeller: false,
          foundingSellerFeeExpiresAt: null,
        });
        return;
      }

      // Check profile for PayPal-based subscription status
      const { data: profile, error } = await supabase
        .from("profiles")
        .select("plan, pro_expires_at, is_founding_seller, founding_seller_fee_expires_at")
        .eq("id", session.user.id)
        .single();

      if (error) throw error;

      const isPro = profile?.plan === "pro" && 
        profile?.pro_expires_at && 
        new Date(profile.pro_expires_at) > new Date();

      // Fetch shipping credit balance and benefits used if Pro
      let shippingCreditBalance = 0;
      let shippingCreditPeriodEnd: string | null = null;
      let benefitsUsedThisMonth = 0;
      let benefitsCapRemaining = PRO_SHIPPING_CONFIG.MONTHLY_BENEFIT_CAP_CENTS;

      if (isPro) {
        const { data: creditData } = await supabase
          .from("pro_shipping_credits")
          .select("credit_balance_cents, period_end, benefits_used_cents")
          .eq("user_id", session.user.id)
          .gte("period_end", new Date().toISOString())
          .order("period_start", { ascending: false })
          .limit(1)
          .single();

        if (creditData) {
          shippingCreditBalance = creditData.credit_balance_cents / 100; // Convert to dollars
          shippingCreditPeriodEnd = creditData.period_end;
          benefitsUsedThisMonth = creditData.benefits_used_cents || 0;
          benefitsCapRemaining = Math.max(0, PRO_SHIPPING_CONFIG.MONTHLY_BENEFIT_CAP_CENTS - benefitsUsedThisMonth);
        }
      }

      setState({
        subscribed: isPro,
        isPro: isPro,
        plan: profile?.plan || "standard",
        subscriptionEnd: profile?.pro_expires_at || null,
        loading: false,
        shippingCreditBalance,
        shippingCreditPeriodEnd,
        benefitsUsedThisMonth,
        benefitsCapRemaining,
        isFoundingSeller: profile?.is_founding_seller || false,
        foundingSellerFeeExpiresAt: profile?.founding_seller_fee_expires_at || null,
      });
    } catch (error) {
      console.error("Error checking subscription:", error);
      setState({ 
        subscribed: false, 
        isPro: false, 
        plan: null, 
        subscriptionEnd: null, 
        loading: false,
        shippingCreditBalance: 0,
        shippingCreditPeriodEnd: null,
        benefitsUsedThisMonth: 0,
        benefitsCapRemaining: PRO_SHIPPING_CONFIG.MONTHLY_BENEFIT_CAP_CENTS,
        isFoundingSeller: false,
        foundingSellerFeeExpiresAt: null,
      });
    }
  };

  const openCheckout = () => {
    // Navigate to the upgrade page for PayPal payment
    window.location.href = "/upgrade/pro";
  };

  const openCustomerPortal = () => {
    // For PayPal-based subscriptions, redirect to upgrade page for management
    toast({
      title: "Manage Subscription",
      description: "Manage your Pro subscription settings on the upgrade page.",
    });
    window.location.href = "/upgrade/pro";
  };

  useEffect(() => {
    checkSubscription();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      checkSubscription();
    });

    // Refresh subscription status every minute
    const interval = setInterval(checkSubscription, 60000);

    return () => {
      subscription.unsubscribe();
      clearInterval(interval);
    };
  }, []);

  return (
    <SubscriptionContext.Provider value={{ ...state, checkSubscription, openCheckout, openCustomerPortal }}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error("useSubscription must be used within a SubscriptionProvider");
  }
  return context;
}